import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/getAuth'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { callAPI } from '../hooks/api'
import AdminCardView from '../components/AdminCardView'
import AdminAddUserCardView from '../components/AdminAddUserCardView'
import FeatureMatrix from './feature-matrix'
import { cn } from '@/lib/utils'

export default function Admin({ localAuth }) {
  const superUserType = 'Hydra Score - Super User'

  const removeStylesheet = (href) => {
    const link = document.querySelector(`link[href="${href}"]`)
    if (link) {
      document.body.removeChild(link)
    }
  }

  removeStylesheet('/css/landing.css')
  removeStylesheet('/css/bootstrap.css')

  const { auth, setAuth, setIsLoggedIn } = useAuth()
  const [selectedPanelName, setSelectedPanelName] = useState('')
  const [adminType, setAdminType] = useState('')
  const [data, setData] = useState({
    wdRating: [
      {
        agencyname: 'agencyname',
        agencyuniq: 'agencyuniq',
        rating: 'rating',
        changed: 0,
      },
    ],
    gwRating: [
      {
        gsa_name: 'gsa_name',
        dwr_gsa_id: 'dwr_gsa_id',
        rating: 'rating',
        changed: 0,
      },
    ],
    executiveGSA: [
      {
        id: 'id',
        subbasin_no: 'subbasin_no',
        gsa_name: 'gsa_name',
        allocation: 'allocation',
        allocation_status_update: 'allocation_status_update',
        changed: 0,
      },
    ],
    executiveSubbasin: [
      {
        id: 'id',
        subbasin_no: 'subbasin_no',
        subbasin_name: 'subbasin_name',
        gsp_status: 'gsp_status',
        subbasin_status_update: 'subbasin_status_update',
        excutive_summary: 'excutive_summary',
        changed: 0,
      },
    ],
  })
  const [selectedMenu, setSelectedMenu] = useState('Analytics') // To track the selected section
  const navigate = useNavigate()

  const logout = () => {
    localStorage.setItem('auth', JSON.stringify({}))
    setAuth(null)
    setIsLoggedIn(false)
    navigate('/')
  }

  useEffect(() => {
    if (localAuth.sessionKey)
      callAPI('/get_admin_type', { session_key: localAuth.sessionKey }).then(
        (res) => {
          if (res.status === 'success') {
            setAdminType(res.admin)
          } else {
            navigate('/')
            toast.warn(res.msg)
          }
        }
      )
  }, [localAuth.sessionKey])

  // Mock function for sorting (can be customized)
  const sortByKey = (key) => {
    return (a, b) => {
      if (a[key] < b[key]) return -1
      if (a[key] > b[key]) return 1
      return 0
    }
  }

  useEffect(() => {
    if (adminType)
      callAPI('/get_admin_data', { session_key: localAuth.sessionKey }).then(
        (res) => {
          if (res.status === 'success') {
            const addChangedKey = (dataList) =>
              dataList.map((item) => ({ ...item, changed: 0 }))
            const sortedData = {
              wdRating: addChangedKey(
                res.data.wdRating.sort(sortByKey('agencyname'))
              ),
              gwRating: addChangedKey(
                res.data.gwRating.sort(sortByKey('gsa_name'))
              ),
              executiveGSA: addChangedKey(
                res.data.executiveGSA.sort(sortByKey('gsa_name'))
              ),
              executiveSubbasin: addChangedKey(
                res.data.executiveSubbasin.sort(sortByKey('subbasin_name'))
              ),
            }
            setData(sortedData)
          }
        }
      )
  }, [adminType])

  const isSuperUser = adminType === superUserType

  // Sidebar component
  const Sidebar = () => {
    return (
      <div
        className={cn('min-h-[600px] lg:min-h-[520px]', {
          'min-h-[460px] lg:min-h-[420px]': !isSuperUser,
        })}
        style={sidebarStyle}
      >
        {/* Add the logo at the top */}
        <div className="flex items-center justify-center pt-5">
          <img src="./logo/hydra_icon.png" alt="Hydra Icon" style={logoStyle} />
        </div>

        <div style={sidebarContentStyle}>
          <button
            style={
              selectedMenu === 'Analytics'
                ? { ...menuButtonStyle, ...selectedButtonStyle }
                : menuButtonStyle
            }
            onClick={() => setSelectedMenu('Analytics')}
          >
            Analytics
          </button>
          {isSuperUser && (
            <button
              style={
                selectedMenu === 'Customers'
                  ? { ...menuButtonStyle, ...selectedButtonStyle }
                  : menuButtonStyle
              }
              onClick={() => setSelectedMenu('Customers')}
            >
              Users
            </button>
          )}
          <button
            style={
              selectedMenu === 'GSA Info'
                ? { ...menuButtonStyle, ...selectedButtonStyle }
                : menuButtonStyle
            }
            onClick={() => setSelectedMenu('GSA Info')}
          >
            GSA Info
          </button>
          <button
            style={
              selectedMenu === 'Water Districts Info'
                ? { ...menuButtonStyle, ...selectedButtonStyle }
                : menuButtonStyle
            }
            onClick={() => setSelectedMenu('Water Districts Info')}
          >
            Water Districts
          </button>
          <button
            style={
              selectedMenu === 'Subbasin Info'
                ? { ...menuButtonStyle, ...selectedButtonStyle }
                : menuButtonStyle
            }
            onClick={() => setSelectedMenu('Subbasin Info')}
          >
            Subbasin Info
          </button>
          <button
            style={
              selectedMenu === 'Executive GSA Info'
                ? { ...menuButtonStyle, ...selectedButtonStyle }
                : menuButtonStyle
            }
            onClick={() => setSelectedMenu('Executive GSA Info')}
          >
            Executive GSA
          </button>
          {isSuperUser && (
            <button
              style={
                selectedMenu === 'Feature Matrix'
                  ? { ...menuButtonStyle, ...selectedButtonStyle }
                  : menuButtonStyle
              }
              onClick={() => setSelectedMenu('Feature Matrix')}
            >
              Feature Matrix
            </button>
          )}
        </div>
        <button style={logoutButtonStyle} onClick={logout}>
          Logout
        </button>
      </div>
    )
  }

  // Render content based on the selected menu
  const renderContent = () => {
    switch (selectedMenu) {
      case 'Analytics':
        return (
          <iframe
            src={`https://analytics.hydrascore.com/?session_key=${localAuth.sessionKey}`}
            title="Analytics Page"
            style={iframeStyle}
          />
        )
      case 'Customers':
        if (superUserType)
          return (
            <div
              key={'addUser'}
              className="max-w-6xl px-3 mt-6 mx-auto min-h-[100px] justify-between items-center shadow-md hover:shadow-lg rounded-md transition-shadow duration-150 m-[10px] overflow-x-auto"
            >
              <AdminAddUserCardView
                sessionKey={localAuth.sessionKey}
                panelName={'addUser'}
                data={data}
                setData={setData}
                logout={logout}
              />
            </div>
          )
        else return <></>
      case 'GSA Info':
        return (
          <>
            <div
              key={'gwRating'}
              className="max-w-6xl px-3 mt-6 mx-auto min-h-[100px] justify-between items-center shadow-md hover:shadow-lg rounded-md transition-shadow duration-150 m-[10px] overflow-x-auto"
            >
              <AdminCardView
                sessionKey={localAuth.sessionKey}
                panelName={'gwRating'}
                data={data}
                setData={setData}
                logout={logout}
              />
            </div>
          </>
        )
      case 'Water Districts Info':
        return (
          <>
            <div
              key={'wdRating'}
              className="max-w-6xl px-3 mt-6 mx-auto min-h-[100px] justify-between items-center shadow-md hover:shadow-lg rounded-md transition-shadow duration-150 m-[10px] overflow-x-auto"
            >
              <AdminCardView
                sessionKey={localAuth.sessionKey}
                panelName={'wdRating'}
                data={data}
                setData={setData}
                logout={logout}
              />
            </div>
          </>
        )
      case 'Subbasin Info':
        return (
          <>
            <div
              key={'executiveSubbasin'}
              className="max-w-6xl px-3 mt-6 mx-auto min-h-[100px] justify-between items-center shadow-md hover:shadow-lg rounded-md transition-shadow duration-150 m-[10px] overflow-x-auto"
            >
              <AdminCardView
                sessionKey={localAuth.sessionKey}
                panelName={'executiveSubbasin'}
                data={data}
                setData={setData}
                logout={logout}
              />
            </div>
          </>
        )
      case 'Executive GSA Info':
        return (
          <>
            <div
              key={'executiveGSA'}
              className="max-w-6xl px-3 mt-6 mx-auto min-h-[100px] justify-between items-center shadow-md hover:shadow-lg rounded-md transition-shadow duration-150 m-[10px] overflow-x-auto"
            >
              <AdminCardView
                sessionKey={localAuth.sessionKey}
                panelName={'executiveGSA'}
                data={data}
                setData={setData}
                logout={logout}
              />
            </div>
          </>
        )
      case 'Feature Matrix':
        if (superUserType)
          return (
            <div
              key={'featureMatrix'}
              className="max-w-6xl px-3 mt-6 mx-auto min-h-[100px] justify-between items-center shadow-md hover:shadow-lg rounded-md transition-shadow duration-150 m-[10px] overflow-x-auto"
            >
              <FeatureMatrix />
            </div>
          )
        else return <></>
      default:
        return <div>Select a menu item from the sidebar</div>
    }
  }

  return (
    <div style={layoutStyle}>
      <Sidebar />
      <div style={contentStyle}>{renderContent()}</div>
    </div>
  )
}

// Basic styles for layout
const layoutStyle = {
  display: 'flex',
  height: '500px',
}
// Adjusted styles for sidebar
const sidebarStyle = {
  marginTop: '100px',
  width: '150px', // Narrower width
  // height: '500px', // Set height to 500px
  backgroundColor: '#1c313f',
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  justifyContent: 'space-between', // Aligns content to top and bottom
  position: 'sticky',
  top: 0,
  borderRadius: '0px 15px 15px 0px', // Rounded corners
}

// Adjusted styles for sidebar menu buttons
const menuButtonStyle = {
  padding: '10px',
  fontSize: '12px', // Smaller font size
  cursor: 'pointer',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  border: '1px solid #ddd',
  borderRadius: '5px',
  transition: 'background-color 0.3s, color 0.3s',
}

// New style for the container of the sidebar buttons (except for the logout button)
const sidebarContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}

// Adjusted styles for the logo at the top of the sidebar
const logoStyle = {
  width: '50px', // Set logo size
  height: '50px',
  marginBottom: '20px', // Space between the logo and the buttons
  alignSelf: 'center', // Center the logo horizontally
}

// Adjusted style for the logout button to stick to the bottom
const logoutButtonStyle = {
  marginTop: 'auto', // Pushes logout button to the bottom
  padding: '10px',
  fontSize: '12px', // Smaller font size
  cursor: 'pointer',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  border: '1px solid #ddd',
  borderRadius: '5px',
  transition: 'background-color 0.3s',
}

// Style for the selected button (highlighted)
const selectedButtonStyle = {
  backgroundColor: '#1c313f', // Change background to match the theme
  color: '#fff', // White text for contrast
  border: '1px solid #fff', // Dark border to blend with the background
  fontWeight: 'bold', // Bold text for emphasis
}

const contentStyle = {
  flex: 1,
  padding: '20px',
}

const iframeStyle = {
  width: '100%',
  height: '90vh',
  border: 'none',
}
